import { compareDecimalStrings, isDecimalString } from "@/shared/utils/decimal";

import {
  CURRENCY_DISPLAY_DECIMALS,
  DEFAULT_SEND_DECIMALS,
  FALLBACK_DISPLAY_DECIMALS,
} from "../constants/cryptoSwaps.constants";
import type { OtcFee, OtcFees, OtcPair } from "../types/cryptoSwaps.types";

export interface AmountPrecision {
  min: number;
  max: number;
}

export type PairPrecision = Pick<OtcPair, "send_currency" | "receive_currency" | "receive_decimals"> &
  Partial<Pick<OtcPair, "send_decimals">>;

/** The pair's own decimals win; the static table covers screens that never load the catalog. */
export function currencyDecimals(currency: string, pair?: PairPrecision | null): number {
  if (pair && currency === pair.send_currency) return pair.send_decimals ?? DEFAULT_SEND_DECIMALS;
  if (pair && currency === pair.receive_currency) return pair.receive_decimals;
  return CURRENCY_DISPLAY_DECIMALS[currency] ?? FALLBACK_DISPLAY_DECIMALS;
}

/** A whole-unit currency shows no decimals; anything finer keeps at least cents so amounts line up. */
export function amountPrecision(currency: string, pair?: PairPrecision | null): AmountPrecision {
  const max = currencyDecimals(currency, pair);
  return { min: Math.min(2, max), max };
}

/** Limits read as round figures: "20", "3.400", never "20,00". */
export function limitPrecision(currency: string, pair?: PairPrecision | null): AmountPrecision {
  return { min: 0, max: currencyDecimals(currency, pair) };
}

/** Intl formats a decimal string exactly, so no float round-trip happens on the way to the screen. */
export function formatAmount(value: string, locale: string, precision: AmountPrecision): string {
  const trimmed = value.trim();
  if (!isDecimalString(trimmed)) return value;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: precision.min,
    maximumFractionDigits: Math.max(precision.min, precision.max),
    useGrouping: true,
  }).format(trimmed as unknown as number);
}

/** Mirrors Intl's half-expand rounding: true exactly when `formatAmount` would print a zero. */
export function roundsToZero(value: string, decimals: number): boolean {
  const trimmed = value.trim();
  if (!isDecimalString(trimmed)) return false;
  const magnitude = trimmed.startsWith("-") ? trimmed.slice(1) : trimmed;
  const half = `0.${"0".repeat(Math.max(0, decimals))}5`;
  return compareDecimalStrings(magnitude, half) < 0;
}

export function visibleFees(
  fees: OtcFees | null | undefined,
  precisionOf: (currency: string) => AmountPrecision,
): OtcFee[] {
  if (!fees) return [];
  return [fees.conversion, fees.delivery].filter(
    (fee): fee is OtcFee =>
      !!fee && typeof fee.total === "string" && !roundsToZero(fee.total, precisionOf(fee.currency).max),
  );
}

export type TemplatePart = { kind: "text"; value: string } | { kind: "slot"; name: string };

export function slotMarker(name: string): string {
  return `%%${name}%%`;
}

/** Splits a sentence translated with `slotMarker` values so each slot renders as a node, in any word order. */
export function splitTemplate(text: string): TemplatePart[] {
  return text
    .split(/%%(\w+)%%/)
    .map(
      (value, index): TemplatePart =>
        index % 2 === 1 ? { kind: "slot", name: value } : { kind: "text", value },
    )
    .filter((part) => part.kind === "slot" || part.value !== "");
}

/** `m:ss`, rounded up so it reads 0:00 only once the time is really gone. */
export function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/** Whole minutes for an estimate, never less than one. */
export function estimateMinutes(seconds: number): number {
  return Math.max(1, Math.round(seconds / 60));
}
